import {
  Button,
  LoadIndicator,
  Popup,
  DateBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { useEffect, useRef, useState } from "react";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import { StockPriceRegister, ValidationResultItem } from "@/types/types";
import { AxiosError } from "axios";
import { useStockPriceRegisterUpdate } from "@/hooks/useStockPriceRegisterUpdate";
import { ClickEvent } from "devextreme/ui/button";
import { RequiredRule } from "devextreme-react/validator";
import ServerResponse from "@/components/ServerResponse";
import { useParams } from "react-router";
import NumberBoxWrapper from "@/components/NumberBoxWrapper";
import { axiosInstance } from "@/helper/axios-instance";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { useAppSelector } from "@/hooks/useAppSelector";
import { NumberBoxRef } from "devextreme-react/cjs/number-box";
import { DateBoxRef } from "devextreme-react/cjs/date-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef<StockPriceRegister, string>>;
};

const validationGroupName = "updateStockPriceRegisterValGroup";

export default function Update({ datagridRef, popupRef }: Props) {
  const params = useParams();
  const id = useAppSelector((state) => state.stockPriceRegister.editId);
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const {
    mutate,
    data: response,
    isPending,
    isError,
    error,
    isSuccess,
  } = useStockPriceRegisterUpdate(params.id as string);
  const [userError, setUserError] = useState<string>("");
  const [anyChange, setAnyChange] = useState(false);
  const valueRef = useRef<NumberBoxRef>(null);
  const timestampRef = useRef<DateBoxRef>(null);

  useEffect(() => {
    if (id) {
      hydrateForm();
    }
  }, [id]);

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    disableInputs(isPending);
  }, [isPending]);

  async function fetchAndSetData() {
    try {
      const response = await axiosInstance({
        method: "GET",
        url: `StockPriceRegister/Get?id=${id}`,
      });
      const dataResponse = response.data.data as StockPriceRegister;

      timestampRef.current?.instance().option("value", dataResponse.timestamp);
      valueRef.current?.instance().option("value", dataResponse.value);
    } catch (error) {
      if (error instanceof AxiosError) setUserError(error.message);
    }

    setAnyChange(false);
  }

  function hydrateForm() {
    fetchAndSetData();
  }

  function disableInputs(state: boolean) {
    timestampRef.current?.instance().option("disabled", state);
    valueRef.current?.instance().option("disabled", state);
  }

  function submit(e: ClickEvent) {
    setServerErrors(null);
    setAnyChange(false);

    if (!e.validationGroup.validate().isValid || !id) return;

    const requestData: Partial<StockPriceRegister> = {
      id: id,
      timestamp: timestampRef.current?.instance().option("value"),
      value: valueRef.current?.instance().option("value"),
    };

    mutate(requestData);
  }

  return (
    <Popup
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <div>
        {userError ? (
          <span>{userError}</span>
        ) : (
          <form className="mx-1 grid grid-cols-1 gap-8">
            <DateBox
              label="Date*"
              ref={timestampRef}
              labelMode="static"
              stylingMode="outlined"
              valueChangeEvent="keyup"
            >
              <Validator validationGroup={validationGroupName}>
                <RequiredRule message={"Date is required"} />
              </Validator>
            </DateBox>
            <NumberBoxWrapper
              ref={valueRef}
              label="Value"
              labelMode="static"
              stylingMode="outlined"
              valueChangeEvent="keyup"
              format="$ #,##0.##"
              showSpinButtons={true}
              showClearButton={true}
            >
              <Validator validationGroup={validationGroupName}>
                <RequiredRule message={"Value is required"} />
              </Validator>
            </NumberBoxWrapper>
          </form>
        )}
      </div>
      <div className="absolute bottom-4 left-4">
        <div className="text-danger">
          {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
        </div>
        <ValidationSummary validationGroup={validationGroupName} id="summary"></ValidationSummary>
        {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
      </div>
      <div className="absolute bottom-4 right-4">
        {anyChange && (
          <Button
            text="reset"
            onClick={hydrateForm}
            useSubmitBehavior={false}
            stylingMode="text"
            className="mr-4"
          />
        )}
        <Button
          validationGroup={validationGroupName}
          useSubmitBehavior={false}
          type="default"
          height={40}
          width={isPending ? 150 : 100}
          onClick={submit}
        >
          <LoadIndicator
            id="default-btn-loading"
            className="mr-3 inline-block align-middle"
            width={25}
            height={25}
            visible={isPending}
          />
          <span className="dx-button-text">{isPending ? "Updating" : "Update"}</span>
        </Button>
      </div>
    </Popup>
  );
}
